import type {
  Callsheet,
  CallsheetRecipient,
  CallsheetTeamMember,
  Resource
} from '__types__';
import CONTACT_INFO_TYPES from 'v1/helpers/DropdownDataSets/CONTACT_INFO_TYPES.json';

export type CallsheetEmailOption = {
  email: string;
  label: string;
};

function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

function isLinkedToTeamMember(
  recipient: CallsheetRecipient,
  teamMemberId: CallsheetTeamMember['id']
): boolean {
  return (
    recipient.callsheet_team_member_id != null &&
    String(recipient.callsheet_team_member_id) === String(teamMemberId)
  );
}

function labelForContactEmailType(value2: string | null | undefined): string {
  const match = CONTACT_INFO_TYPES.find(type => type.value === value2);
  return match?.label ?? 'Other';
}

/**
 * Build selectable email options for a callsheet team member:
 * contact emails + agent emails from represented_by, deduped by address.
 */
export function getCallsheetTeamMemberEmailOptions(
  contact: Resource | null | undefined
): CallsheetEmailOption[] {
  if (!contact) return [];

  const optionsByEmail = new Map<string, CallsheetEmailOption>();

  const addOption = (email: string | null | undefined, label: string) => {
    const trimmed = email?.trim();
    if (!trimmed) return;
    const key = normalizeEmail(trimmed);
    if (optionsByEmail.has(key)) return;
    optionsByEmail.set(key, { email: trimmed, label });
  };

  for (const detail of contact.emails ?? []) {
    addOption(detail.value_1, labelForContactEmailType(detail.value_2));
  }

  for (const detail of contact.represented_by?.emails ?? []) {
    addOption(detail.value_1, 'Agent');
  }

  return Array.from(optionsByEmail.values());
}

/** First selectable email for a new team-member recipient (contact, then agent). */
export function getDefaultCallsheetTeamMemberEmail(
  contact: Resource | null | undefined
): string | null {
  return getCallsheetTeamMemberEmailOptions(contact)[0]?.email ?? null;
}

/**
 * Payload rows for creating nested team-member recipients.
 * Prefer recipient emails; otherwise one row from fallbackEmail (may omit address).
 */
export function buildTeamMemberCreateRecipients(input: {
  contactId: number;
  recipients?: Array<{ email_address?: string | null }>;
  fallbackEmail?: string | null;
}): Array<{ contact_id: number; email_address?: string }> {
  const emails = (input.recipients ?? [])
    .map(recipient => recipient.email_address?.trim())
    .filter((email): email is string => Boolean(email));

  if (emails.length > 0) {
    return emails.map(email_address => ({
      contact_id: input.contactId,
      email_address
    }));
  }

  const fallback = input.fallbackEmail?.trim();
  return [
    {
      contact_id: input.contactId,
      ...(fallback ? { email_address: fallback } : {})
    }
  ];
}

/** Flat callsheet.recipients rows linked to one team member. */
export function recipientsForTeamMember(
  recipients: CallsheetRecipient[] | undefined,
  teamMemberId: CallsheetTeamMember['id'] | null | undefined
): CallsheetRecipient[] {
  if (teamMemberId == null) return [];
  return (recipients ?? []).filter(recipient =>
    isLinkedToTeamMember(recipient, teamMemberId)
  );
}

export function getSelectedEmailsForTeamMember(input: {
  recipients: CallsheetRecipient[];
  teamMemberId: CallsheetTeamMember['id'];
}): string[] {
  return recipientsForTeamMember(input.recipients, input.teamMemberId)
    .map(recipient => recipient.email_address?.trim())
    .filter((email): email is string => Boolean(email));
}

/**
 * Merge selected emails for one team member into the full recipients list.
 * Unchecked emails are omitted (same as Delete recipient on share) — including
 * rows that already have notifications.
 */
export function syncTeamMemberRecipientEmails(input: {
  recipients: CallsheetRecipient[];
  callsheetId: NonNullable<Callsheet['id']>;
  teamMemberId: CallsheetTeamMember['id'];
  contactId: NonNullable<CallsheetTeamMember['contact_id']>;
  selectedEmails: string[];
}): CallsheetRecipient[] {
  const selectedByNormalized = new Map<string, string>();
  for (const email of input.selectedEmails) {
    const key = normalizeEmail(email);
    if (key && !selectedByNormalized.has(key)) {
      selectedByNormalized.set(key, email.trim());
    }
  }

  const teamRecipients = recipientsForTeamMember(
    input.recipients,
    input.teamMemberId
  );
  const otherRecipients = input.recipients.filter(
    recipient => !isLinkedToTeamMember(recipient, input.teamMemberId)
  );

  const keptTeamRecipients: CallsheetRecipient[] = [];
  const coveredEmails = new Set<string>();

  for (const recipient of teamRecipients) {
    const key = normalizeEmail(recipient.email_address);
    if (key === '' || !selectedByNormalized.has(key)) continue;
    keptTeamRecipients.push(recipient);
    coveredEmails.add(key);
  }

  for (const [key, email] of selectedByNormalized) {
    if (coveredEmails.has(key)) continue;
    keptTeamRecipients.push({
      callsheet_id: input.callsheetId,
      contact_id: input.contactId,
      callsheet_team_member_id: input.teamMemberId,
      email_address: email
    } as CallsheetRecipient);
  }

  return [...otherRecipients, ...keptTeamRecipients];
}
